import React from "react";
import { Row, Col, Card, Badge } from "react-bootstrap";
import {
  FaChartBar,
  FaDollarSign,
  FaShoppingCart,
  FaChartLine,
  FaTrophy,
} from "react-icons/fa";
import ReportCard from "./ReportCard";
import {
  PieChartJS,
  generateChartColors,
  prepareChartData,
} from "./ChartComponents";

const SalesReport = ({ dashboardData, formatCurrency }) => {
  const orderTypeChartData = prepareChartData(
    (dashboardData.sales.orderTypeBreakdown || []).map((type) => type.type),
    [
      {
        label: "Sales Amount",
        data: (dashboardData.sales.orderTypeBreakdown || []).map(
          (type) => type.amount
        ),
      },
    ],
    generateChartColors((dashboardData.sales.orderTypeBreakdown || []).length)
  );

  // Calculate additional metrics
  const avgOrderValue =
    dashboardData.sales.ordersCount > 0
      ? dashboardData.sales.totalRevenue / dashboardData.sales.ordersCount
      : 0;

  const totalOrderTypes = (dashboardData.sales.orderTypeBreakdown || []).length;

  return (
    <>
      <Row className="g-4 mb-4">
        <Col lg={3}>
          <ReportCard
            title="Total Revenue"
            value={formatCurrency(dashboardData.sales.totalRevenue)}
            icon={<FaDollarSign />}
            color="success"
            subtitle="All sales combined"
          />
        </Col>
        <Col lg={3}>
          <ReportCard
            title="Total Orders"
            value={dashboardData.sales.ordersCount.toLocaleString()}
            icon={<FaShoppingCart />}
            color="info"
            subtitle="Orders processed"
          />
        </Col>
        <Col lg={3}>
          <ReportCard
            title="Avg. Order Value"
            value={formatCurrency(avgOrderValue)}
            icon={<FaChartLine />}
            color="primary"
            subtitle="Per order average"
          />
        </Col>
        <Col lg={3}>
          <ReportCard
            title="Order Types"
            value={totalOrderTypes.toString()}
            icon={<FaChartBar />}
            color="warning"
            subtitle="Different order categories"
          />
        </Col>
      </Row>
      <Row className="g-4">
        <Col lg={8}>
          <Card className="shadow-sm border-0 mb-5">
            <Card.Header className="bg-transparent">
              <h5 className="mb-0">
                <FaTrophy className="me-2 text-white" />
                Top Selling Products by Quantity
              </h5>
              <small className="text-muted">
                Ranked by quantity sold (highest first)
              </small>
            </Card.Header>
            <Card.Body>
              {dashboardData.sales.topProducts.length > 0 ? (
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead>
                      <tr>
                        <th>Rank</th>
                        <th>Product Name</th>
                        <th>Quantity Sold</th>
                        <th>Sales Amount</th>
                        <th>Orders Count</th>
                        <th>Avg per Order</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dashboardData.sales.topProducts.map((product, index) => (
                        <tr key={index}>
                          <td>
                            <Badge
                              bg={index < 3 ? "warning" : "secondary"}
                              className="fs-6"
                            >
                              #{index + 1}
                            </Badge>
                          </td>
                          <td className="fw-medium">{product.name}</td>
                          <td>
                            <div className="d-flex align-items-center">
                              <Badge bg="success" className="me-2">
                                {(product.quantity || 0).toLocaleString()}
                              </Badge>
                              <small className="text-muted">units</small>
                            </div>
                          </td>
                          <td className="fw-medium text-success">
                            {formatCurrency(product.sales)}
                          </td>
                          <td>
                            <Badge bg="primary">{product.orders}</Badge>
                          </td>
                          <td>
                            <small className="text-muted">
                              {product.orders > 0
                                ? (product.quantity / product.orders).toFixed(1)
                                : "0"}{" "}
                              units/order
                            </small>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-4">
                  <FaTrophy size={60} className="text-muted mb-3" />
                  <p className="text-muted">No sales data available</p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col lg={4}>
          <PieChartJS
            data={orderTypeChartData}
            title="Sales by Order Type"
            height={350}
            options={{
              plugins: {
                legend: {
                  position: "bottom",
                },
                tooltip: {
                  callbacks: {
                    label: function (context) {
                      const label = context.label || "";
                      const value = formatCurrency(context.parsed);
                      const total = context.dataset.data.reduce(
                        (a, b) => a + b,
                        0
                      );
                      const percentage =
                        total > 0
                          ? ((context.parsed / total) * 100).toFixed(1)
                          : "0";
                      return `${label}: ${value} (${percentage}%)`;
                    },
                  },
                },
              },
            }}
          />
        </Col>
      </Row>
    </>
  );
};

export default SalesReport;
